import { usePage } from "@inertiajs/react"
import { HeroFilm, type HeroPhoto } from "@/components/gisela/HeroBand"
import { EnquiryForm } from "@/components/gisela/EnquiryForm"
import { BrandMotif } from "@/components/ui/brand-motif"
import { useLocalePath } from "@/lib/locale-path"
import type { PageProps, PlacementPhoto } from "@/types/inertia"

// The two bands Gisela's pages close on, shared so every page ends the same
// way rather than each one growing its own copy of the markup.

interface ContactBandProps {
  heading: string
  body: string
  /** The band's background; nil leaves it on plain teal, as most pages are. */
  photo?: HeroPhoto | null
  /** Where the one ask goes — the booking page unless a page says otherwise. */
  href?: string
  label?: string
}

// The teal invitation: one heading, one line, one button. The rings motif and
// the film over the photo are what make it the same band on every page.
export function ContactBand({ heading, body, photo, href = "/book", label }: ContactBandProps) {
  const { props } = usePage<PageProps>()
  const lp = useLocalePath()

  return (
    <section className="relative overflow-hidden bg-teal py-16 text-white">
      <HeroFilm photo={photo} />
      <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
      <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
        <h2 className="text-white">{heading}</h2>
        <p className="mt-3 text-white/75">{body}</p>
        <div className="mt-7">
          <a
            href={lp(href)}
            className="rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
          >
            {label ?? props.translations.gisela_nav.book_call}
          </a>
        </div>
      </div>
    </section>
  )
}

interface PhotoEnquiryBandProps {
  /** Admin copy for this page, falling back to the shared wording. */
  heading?: string
  body?: string
  photo?: PlacementPhoto | null
  /**
   * Which of the two shared wordings to fall back to. Most pages talk about
   * Gisela ("she reads every message"); the OCD pillar and its subtypes are
   * written in her own voice and say "I read every message".
   */
  voice?: "hers" | "about-her"
}

// A picture beside the short enquiry form — the last thing on the page, for
// the reader who has got this far and would rather just ask. The picture is
// the home hero's disc, at the size this column can hold: the same circle,
// the same crescent of line, so the page ends on the shape it opened the site
// with. It used to be a tall rounded rectangle nearly the height of a screen,
// which was right for a room photograph and far too much cream around a
// portrait — and around nothing at all, on a page whose slot is still empty.
export function PhotoEnquiryBand({ heading, body, photo, voice = "about-her" }: PhotoEnquiryBandProps) {
  const { props } = usePage<PageProps>()
  const tc = props.translations.gisela_pages.common
  const tf = props.translations.therapy.enquiry_form
  // The enquiry posts back to this page, so the thank-you replaces the form
  // in place rather than sending the reader anywhere.
  const notice = props.flash.notice
  const ownVoice = voice === "hers"
  const defaultHeading = ownVoice ? tc.contact_heading_first_person : tc.contact_heading
  const defaultBody = ownVoice ? tc.contact_body_first_person : tc.contact_body

  return (
    <section id="enquiry-form" className="relative overflow-hidden bg-cream py-14 lg:py-16">
      {/* The disc leaves the cream around it bare in a way the tall frame
          never did, so the band takes a mark of its own — the brush speckle,
          the same one that sits beside her portrait on the home page. Low and
          left, behind the picture: one per section, never over a face, never
          on the form. */}
      <BrandMotif variant="speckle" size={340} className="absolute bottom-2 -left-8 lg:bottom-6 lg:left-4" />
      <div className="relative grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:items-center lg:gap-14">
        {/* The home hero's disc: the picture in a circle with the same crescent
            of line clearing it up and to the left. The line is sage rather
            than the hero's white — on cream, white is not there at all. */}
        <div className="px-6 lg:pl-12">
          <div className="relative mx-auto aspect-square w-full max-w-[22rem] sm:max-w-[26rem] lg:max-w-[30rem]">
            <div
              aria-hidden
              className="absolute -top-[3.4%] -left-[4.5%] h-full w-full rounded-full border-2 border-sage"
            />
            <div className="relative h-full w-full overflow-hidden rounded-full shadow-lg">
              {photo ? (
                <img
                  src={photo.portrait_url ?? photo.card_url}
                  alt={photo.alt_text ?? ""}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                /* Nothing placed yet — the hero's own empty state, a plain
                   field of sage rather than a broken frame. */
                <div aria-hidden className="h-full w-full bg-sage-light" />
              )}
            </div>
          </div>
        </div>

        <div className="px-6 lg:px-8 lg:pr-12">
          <h2>{heading || defaultHeading}</h2>
          {notice ? (
            <div className="mt-6 rounded-2xl bg-white p-8 ring-1 ring-hairline">
              <h3>{tf.thank_you}</h3>
              <p className="mt-3 text-ink-muted">{notice}</p>
            </div>
          ) : (
            <>
              <p className="mt-3 text-ink-muted">{body || defaultBody}</p>
              <EnquiryForm compact />
            </>
          )}
        </div>
      </div>
    </section>
  )
}
